import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllPendingReports, removePendingReport } from '@/lib/offlineStorage';
import { submitReport } from '@/lib/reportService';
import { supabase } from '@/lib/supabase';

export function useSync() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSyncing, setIsSyncing] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const isCheckingRef = useRef(false);

    const updatePendingCount = useCallback(async () => {
        const reports = await getAllPendingReports();
        const pendingSubmissions = reports.filter(r => r.status === 'PENDING_SUBMISSION');
        setPendingCount(pendingSubmissions.length);
    }, []);

    const syncReports = useCallback(async () => {
        if (!navigator.onLine || isSyncing) return;

        const allReports = await getAllPendingReports();
        const reports = allReports.filter(r => r.status === 'PENDING_SUBMISSION');

        if (reports.length === 0) return;

        setIsSyncing(true);

        const TIMEOUT_MS = 20000; // 20 seconds timeout

        for (const report of reports) {
            try {
                // Wrap submission in a timeout
                await Promise.race([
                    submitReport({
                        objectDesc: report.objectDesc,
                        sceneDesc: report.sceneDesc,
                        severity: report.severity,
                        latitude: report.location?.latitude,
                        longitude: report.location?.longitude,
                        municipality: report.location?.municipality,
                        imageFile: report.imageBlob,
                        category: (report as any).category
                    }),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Sync timeout')), TIMEOUT_MS)
                    )
                ]);

                await removePendingReport(report.id);
            } catch (err: any) {
                console.error(`Failed to sync report ${report.id}:`, err);

                // If the error is likely permanent (400 Bad Request, 413 Payload Too Large, 429 Rate Limit), 
                // remove it to prevent clogging the queue forever.
                // Note: 429 might be temporary, but if we don't handle backoff, it's better to fail than hang loop.
                // For now, we'll keep 429s but remove malformed requests.
                const errorMessage = err.message || '';
                if (errorMessage.includes('400') || errorMessage.includes('413') || errorMessage.includes('invalid')) {
                    console.warn(`Removing permanently failed report ${report.id}`);
                    await removePendingReport(report.id);
                }
            }
        }

        await updatePendingCount();
        setIsSyncing(false);
    }, [isSyncing, updatePendingCount]);

    const checkConnectivity = useCallback(async () => {
        if (isCheckingRef.current) return;
        isCheckingRef.current = true;

        try {
            // Heartbeat check
            const { error } = await supabase.auth.getSession();
            setIsOnline(!error);
            if (!error) {
                syncReports();
            }
        } catch {
            setIsOnline(false);
        } finally {
            isCheckingRef.current = false;
        }
    }, [syncReports]);

    useEffect(() => {
        const handleOnline = () => {
            checkConnectivity();
        };
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Initial check and periodic heartbeat
        updatePendingCount();
        checkConnectivity();

        const heartbeatInterval = setInterval(checkConnectivity, 30000);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(heartbeatInterval);
        };
    }, [checkConnectivity, updatePendingCount]);

    return {
        isOnline,
        isSyncing,
        pendingCount,
        syncReports
    };
}
